import React, { useState, useEffect, useCallback, useMemo } from 'react';
import athenaService from '../services/athenaService';

const fmtBytes = (b) => {
    if (b == null) return '';
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0, n = Number(b);
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(i > 0 && n < 100 ? 2 : 0)} ${u[i]}`;
};

export default function AthenaEditor() {
    const [databases, setDatabases] = useState([]);
    const [database, setDatabase] = useState('');
    const [tables, setTables] = useState([]);
    const [tableFilter, setTableFilter] = useState('');
    const [loadingDbs, setLoadingDbs] = useState(false);
    const [loadingTables, setLoadingTables] = useState(false);

    const [sql, setSql] = useState('');
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null); // { columns, rows, dataScannedBytes, engineExecutionTimeMs }

    useEffect(() => {
        setLoadingDbs(true);
        athenaService.listDatabases()
            .then(r => setDatabases(r.data?.databases || []))
            .catch(e => setError(e.response?.data?.error?.message || e.message))
            .finally(() => setLoadingDbs(false));
    }, []);

    useEffect(() => {
        if (!database) { setTables([]); return; }
        setLoadingTables(true);
        setTableFilter('');
        athenaService.listTables(database)
            .then(r => setTables(r.data?.tables || []))
            .catch(e => setError(e.response?.data?.error?.message || e.message))
            .finally(() => setLoadingTables(false));
    }, [database]);

    const filteredTables = useMemo(
        () => tables.filter(t => t.toLowerCase().includes(tableFilter.toLowerCase())),
        [tables, tableFilter]
    );

    const insertTableQuery = (t) => setSql(`SELECT * FROM "${database}"."${t}" limit 10;`);

    const runQuery = useCallback(async () => {
        if (!sql.trim()) return;
        setRunning(true); setError(null); setResult(null);
        try {
            const start = await athenaService.runQuery({ database, sql });
            const id = start.data?.queryExecutionId;
            const t0 = Date.now();
            const poll = async () => {
                try {
                    const s = await athenaService.queryStatus(id, 500);
                    const d = s.data;
                    if (d.state === 'SUCCEEDED') { setRunning(false); setResult(d); return; }
                    if (d.state === 'FAILED' || d.state === 'CANCELLED') { setRunning(false); setError(d.reason || `Query ${d.state}`); return; }
                } catch (_) { /* keep polling */ }
                if (Date.now() - t0 > 10 * 60 * 1000) { setRunning(false); setError('Timed out waiting for query'); return; }
                setTimeout(poll, 1500);
            };
            setTimeout(poll, 1000);
        } catch (e) {
            setRunning(false); setError(e.response?.data?.error?.message || e.message);
        }
    }, [sql, database]);

    return (
        <div className="acp-page" style={{ maxWidth: 1600 }}>
            <div className="acp-header">
                <div>
                    <h1 className="acp-title">Athena</h1>
                    <p className="acp-subtitle">Query the vault data lake</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                {/* Left: database + tables */}
                <div className="acp-card" style={{ width: 300, flexShrink: 0 }}>
                    <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div>
                            <div className="detail-label" style={{ marginBottom: 4 }}>Database</div>
                            <select className="acp-input" style={{ width: '100%' }} value={database} onChange={e => setDatabase(e.target.value)} disabled={loadingDbs}>
                                <option value="">{loadingDbs ? 'Loading…' : 'Select database…'}</option>
                                {databases.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="detail-label" style={{ marginBottom: 4 }}>Tables{tables.length ? ` (${tables.length})` : ''}</div>
                            <input className="acp-input" style={{ width: '100%', marginBottom: 8 }} placeholder="Filter tables…" value={tableFilter} onChange={e => setTableFilter(e.target.value)} disabled={!database} />
                            <div style={{ maxHeight: '55vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                                {loadingTables ? <div style={{ padding: 8, color: '#6b7280' }}>Loading…</div>
                                    : !database ? <div style={{ padding: 8, color: '#6b7280' }}>Select a database</div>
                                    : filteredTables.length === 0 ? <div style={{ padding: 8, color: '#6b7280' }}>No tables</div>
                                    : filteredTables.map(t => (
                                        <button key={t} onClick={() => insertTableQuery(t)} title="Insert SELECT for this table"
                                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', border: 'none', borderBottom: '1px solid #f3f4f6', background: 'none', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                            {t}
                                        </button>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: editor + results */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div className="acp-card">
                        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <textarea className="acp-input"
                                style={{ width: '100%', minHeight: 160, fontFamily: 'monospace', fontSize: '0.9rem', resize: 'vertical' }}
                                placeholder={'SELECT * FROM "vault_118"."reservation_name" limit 10;'}
                                value={sql} onChange={e => setSql(e.target.value)} />
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <button className="acp-btn acp-btn-primary" onClick={runQuery} disabled={running || !sql.trim()}>{running ? 'Running…' : 'Run'}</button>
                                <button className="acp-btn acp-btn-secondary" onClick={() => { setSql(''); setResult(null); setError(null); }} disabled={running}>Clear</button>
                                {result && (
                                    <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                                        {result.rows?.length ?? 0} rows · {fmtBytes(result.dataScannedBytes)} scanned
                                        {result.engineExecutionTimeMs != null ? ` · ${(result.engineExecutionTimeMs / 1000).toFixed(2)}s` : ''}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {error && <div className="os-msg os-msg-error">{error}</div>}

                    {result && (
                        <div className="acp-card">
                            <div style={{ overflowX: 'auto' }}>
                                <table className="acp-table" style={{ fontSize: '0.82rem' }}>
                                    <thead><tr>{result.columns.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
                                    <tbody>
                                        {result.rows.map((r, ri) => (
                                            <tr key={ri}>
                                                {r.map((v, ci) => (
                                                    <td key={ci} style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v == null ? '' : String(v)}>
                                                        {v == null ? '' : String(v)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {result.rows.length === 0 && <div style={{ padding: 12, color: '#6b7280' }}>Query completed — no rows returned.</div>}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
